/**
 * Location formatting utilities
 *
 * IMPORTANT: For displaying date/time/location information across the app,
 * ALWAYS use formatConnectionMessage(). This is the single source of truth
 * for consistent formatting everywhere.
 *
 * SCHEMA: Radar provider format via venueInfo.provider, venueInfo.venue.*, venueInfo.address.*
 */

import { DAYS_SHORT, MONTHS_SHORT, formatTime12h, formatFriendlyTime12h } from './dateHelpers';

/**
 * Extract location components from connection metadata
 * @param {Object} connectionMetadata - Connection metadata containing locationData
 * @returns {Object} - Normalized location components
 */
const extractLocationComponents = (connectionMetadata) => {
  const defaults = {
    venueName: '',
    streetNumber: '',
    streetName: '',
    neighborhood: '',
    city: '',
    cityAbbr: '',
  };

  if (!connectionMetadata?.locationData?.venueInfo) {
    // No structured data, try to parse from location string
    if (connectionMetadata?.location) {
      return parseLocationString(connectionMetadata.location);
    }
    return defaults;
  }

  const venueInfo = connectionMetadata.locationData.venueInfo;

  return {
    venueName: venueInfo.venue?.name || '',
    streetNumber: venueInfo.address?.streetNumber || '',
    streetName: venueInfo.address?.street || '',
    neighborhood: venueInfo.address?.neighborhood || '',
    city: venueInfo.address?.city || '',
    cityAbbr: venueInfo.address?.cityAbbr || '',
  };
};

/**
 * Parse location string as fallback
 * @param {string} locationStr - Location string like "7 E 85th St, NYC"
 * @returns {Object} - Parsed location components
 */
const parseLocationString = (locationStr) => {
  const defaults = {
    venueName: '',
    streetNumber: '',
    streetName: '',
    neighborhood: '',
    city: '',
    cityAbbr: '',
  };

  if (!locationStr) return defaults;

  const parts = locationStr.split(',').map((p) => p.trim());

  if (parts.length === 0) return defaults;

  // First part - could be venue, address, or just text
  const firstPart = parts[0];
  const hasNumbers = /^\d/.test(firstPart);

  if (hasNumbers) {
    // Starts with number, likely an address
    const addressParts = firstPart.split(' ');
    defaults.streetNumber = addressParts[0] || '';
    defaults.streetName = addressParts.slice(1).join(' ') || '';
  } else {
    // No leading number - could be venue or neighborhood
    defaults.venueName = firstPart;
  }

  // Second part - could be neighborhood or city
  if (parts.length > 1) {
    defaults.neighborhood = parts[1];
  }

  // Third part - city
  if (parts.length > 2) {
    defaults.city = parts[2];
    defaults.cityAbbr = getCityAbbreviation(parts[2]);
  }

  return defaults;
};

// ===== ABBREVIATION HELPERS =====

/**
 * Abbreviate street words (Street → St, Avenue → Ave, etc.)
 * @param {string} str - String containing street words
 * @returns {string} - String with abbreviated street words
 */
const abbreviateStreetWords = (str) => {
  if (!str) return '';
  return str
    .replace(/\bStreet\b/gi, 'St')
    .replace(/\bAvenue\b/gi, 'Ave')
    .replace(/\bBoulevard\b/gi, 'Blvd')
    .replace(/\bDrive\b/gi, 'Dr')
    .replace(/\bRoad\b/gi, 'Rd')
    .replace(/\bLane\b/gi, 'Ln')
    .replace(/\bCourt\b/gi, 'Ct')
    .replace(/\bPlace\b/gi, 'Pl')
    .replace(/\bSquare\b/gi, 'Sq')
    .replace(/\bHighway\b/gi, 'Hwy')
    .replace(/\bParkway\b/gi, 'Pkwy');
};

/**
 * Get abbreviation for major cities
 * @param {string} cityName - Full city name
 * @returns {string} - City abbreviation or original city name if not found
 */
export const getCityAbbreviation = (cityName) => {
  if (!cityName) return '';

  const cityMap = {
    'new york': 'NYC',
    'new york city': 'NYC',
    'los angeles': 'LA',
    'san francisco': 'SF',
    chicago: 'Chi',
    philadelphia: 'Phila',
    washington: 'DC',
    'las vegas': 'LV',
    houston: 'HOU',
    denver: 'DEN',
    boston: 'BOS',
    'new orleans': 'NO',
    jacksonville: 'Jax',
    'san antonio': 'SA',
    detroit: 'Det',
    seattle: 'SEA',
    portland: 'PDX',
    miami: 'MIA',
    atlanta: 'ATL',
    'kansas city': 'KC',
    london: 'LDN',
    tokyo: 'TYO',
    'hong kong': 'HK',
    'mexico city': 'MEX',
    'rio de janeiro': 'Rio',
    singapore: 'SG',
  };

  const normalizedName = cityName.toLowerCase();
  return cityMap[normalizedName] || cityName;
};

/**
 * Read a date's wall-clock fields, optionally pinned to a stored UTC offset.
 *
 * With an offset (minutes east of UTC, e.g. -240 for EDT), the fields are the
 * wall clock at the place the moment happened — a date shifted by the offset
 * and read via getUTC* getters. Without one, the viewer's local wall clock.
 * Age comparisons must use the true instant (date.getTime()), never these fields.
 *
 * @param {Date} date - The true instant
 * @param {number} [utcOffsetMinutes] - Minutes east of UTC captured at the event
 * @param {boolean} [options.friendlyTime] - Rounded half-hour time, no colon
 * @returns {{dayOfWeek: string, month: string, day: number, year: number, timeStr: string}}
 */
const getWallClockParts = (date, utcOffsetMinutes, { friendlyTime = false } = {}) => {
  const timeFormatter = friendlyTime ? formatFriendlyTime12h : formatTime12h;
  if (Number.isFinite(utcOffsetMinutes)) {
    const shifted = new Date(date.getTime() + utcOffsetMinutes * 60000);
    return {
      dayOfWeek: DAYS_SHORT[shifted.getUTCDay()],
      month: MONTHS_SHORT[shifted.getUTCMonth()],
      day: shifted.getUTCDate(),
      year: shifted.getUTCFullYear(),
      timeStr: timeFormatter(shifted, { useUTC: true }),
    };
  }
  return {
    dayOfWeek: DAYS_SHORT[date.getDay()],
    month: MONTHS_SHORT[date.getMonth()],
    day: date.getDate(),
    year: date.getFullYear(),
    timeStr: timeFormatter(date),
  };
};

/**
 * Format date and time for display
 * Returns: "Mon Jan 15, 2:30pm" — the year renders only when it isn't the
 * current year ("Mon Jan 15, 2024, 2:30pm"), aligning with the shipped
 * formatRelativeTime rule. A date-only value (e.g. meetings.met_date) formats
 * without a time and must not be parsed as a Date (UTC-midnight math shifts
 * the day for viewers west of UTC).
 * @param {string|Date} dateString - Date to format
 * @param {number} [utcOffsetMinutes] - Pin to the event's local wall clock (minutes east of UTC)
 * @param {boolean} [options.friendlyTime] - Rounded half-hour time, no colon —
 *   "2pm" / "230pm" (the Anon WE MET card; everywhere else keeps exact time)
 * @returns {string} - Formatted date string
 */
export const formatDateTimeForDisplay = (
  dateString,
  utcOffsetMinutes,
  { friendlyTime = false } = {}
) => {
  if (!dateString) return '';

  try {
    // Date.now() (not new Date()) — the age/year rungs in this file all read
    // the clock this way, so tests can pin "now" with one mock
    const currentYear = new Date(Date.now()).getFullYear();

    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      // Date.UTC + getUTCDay: day-of-week from calendar fields alone, no zone math
      const dayOfWeek = DAYS_SHORT[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
      const yearPart = year !== currentYear ? `, ${year}` : '';
      return `${dayOfWeek} ${MONTHS_SHORT[month - 1]} ${day}${yearPart}`;
    }

    const date = new Date(dateString);
    const { dayOfWeek, month, day, year, timeStr } = getWallClockParts(date, utcOffsetMinutes, {
      friendlyTime,
    });

    const yearPart = year !== currentYear ? `, ${year}` : '';
    return `${dayOfWeek} ${month} ${day}${yearPart}, ${timeStr}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format connection message for detail views
 * Format:
 *   {date and time}
 *   {venue}, {number} {street}, {neighborhood}, {cityAbbr}
 *
 * All location info on ONE line after the date.
 *
 * @param {Object} connectionMetadata - Connection metadata containing location and date information
 * @returns {string} - Formatted string for detail view display
 */
export const formatConnectionMessage = (connectionMetadata) => {
  if (!connectionMetadata) {
    return '';
  }

  try {
    // Extract date/time
    let dateTimeStr = '';
    if (connectionMetadata.meetingDate) {
      dateTimeStr = formatDateTimeForDisplay(connectionMetadata.meetingDate);
    }

    const { venueName, streetNumber, streetName, neighborhood, cityAbbr, city } =
      extractLocationComponents(connectionMetadata);

    // Apply abbreviations
    const abbreviatedStreet = abbreviateStreetWords(streetName);
    const displayCity = cityAbbr || getCityAbbreviation(city);

    // Build location line: {venue}, {number} {street}, {neighborhood}, {cityAbbr}
    // All on ONE line, skip missing parts gracefully
    const locationParts = [];

    // Venue
    if (venueName) {
      locationParts.push(venueName);
    }

    // Street with number
    if (streetNumber && abbreviatedStreet) {
      locationParts.push(`${streetNumber} ${abbreviatedStreet}`);
    } else if (abbreviatedStreet) {
      locationParts.push(abbreviatedStreet);
    }

    // Neighborhood
    if (neighborhood) {
      locationParts.push(neighborhood);
    }

    // City
    if (displayCity) {
      locationParts.push(displayCity);
    }

    const locationLine = locationParts.join(', ');

    // Combine: date on line 1, location on line 2
    const lines = [];
    if (dateTimeStr) lines.push(dateTimeStr);
    if (locationLine) lines.push(locationLine);

    return lines.join('\n');
  } catch (error) {
    console.error('Error formatting connection message:', error);
    return '';
  }
};

/**
 * Format location for short display (e.g., connection list cards)
 *
 * Rules:
 * 1. Venue + Neighborhood: "Starbucks, Upper East Side, NYC"
 * 2. Venue, no Neighborhood: "Starbucks, 1144 Madison Ave, NYC"
 * 3. No Venue + Neighborhood: "1144 Madison Ave, Upper East Side, NYC"
 * 4. No Venue + No Neighborhood: "1144 Madison Ave, NYC"
 *
 * @param {Object} connectionMetadata - Connection metadata containing location information
 * @returns {string} - Short formatted location string
 */
export const formatLocationShort = (connectionMetadata) => {
  if (!connectionMetadata) return '';

  try {
    const { venueName, streetNumber, streetName, neighborhood, cityAbbr } =
      extractLocationComponents(connectionMetadata);

    const abbreviatedStreet = abbreviateStreetWords(streetName);
    const streetAddress =
      streetNumber && abbreviatedStreet
        ? `${streetNumber} ${abbreviatedStreet}`
        : abbreviatedStreet || '';

    const components = [];

    if (venueName) {
      // Has venue
      components.push(venueName);
      if (neighborhood) {
        // Venue + Neighborhood + City
        components.push(neighborhood);
      } else if (streetAddress) {
        // Venue + Street Address (no neighborhood)
        components.push(streetAddress);
      }
    } else if (streetAddress) {
      // No venue - always include street address
      components.push(streetAddress);
      if (neighborhood) {
        // Street + Neighborhood
        components.push(neighborhood);
      }
    }

    // Always add city at the end
    if (cityAbbr) {
      components.push(cityAbbr);
    }

    if (components.length > 0) {
      return components.join(', ');
    }

    // Ultimate fallback to stored location string
    return connectionMetadata.location || '';
  } catch (error) {
    console.error('Error formatting short location:', error);
    return connectionMetadata.location || '';
  }
};

/**
 * Format system message for chat display (FrostedPill style)
 *
 * Venue gate (exchange contract #7, isVenueGated): on Anon↔Profile and PXP
 * the venue name displays only after that side's human confirmation
 * (`venueConfirmed` in the metadata). Until then the banner shows
 * neighborhood → city → '' (time-only renders from meetingDate). PXP banners
 * are per-user: each side confirms its own.
 *
 * Before venue confirmed:
 *   "Williamsburg"           (neighborhood)
 *   "Sun 1:47pm"             (abbreviated day + time)
 *
 * After venue confirmed:
 *   "Outro"                  (venue name)
 *   "Sun 1:47pm"             (abbreviated day + time)
 *
 * @param {Object} connectionMetadata - Connection metadata containing location and date information
 * @returns {string} - Formatted string for system message in chat
 */
/**
 * The venue consent gate (exchange contract #7), ONE predicate: on any
 * exchanged connection (Anon↔Profile and PXP) the venue name displays only
 * after that side's human confirmation — the system suggests a venue, it
 * never picks one (Mark, 2026-10-16). A manual add has no exchangeType: its
 * place was typed by the person, so it is never gated. Every venue-showing
 * surface routes through this — the chat banner and People card via the
 * formatters below, the Detail page via gateVenueMetadata (its renderer
 * reads metadata raw), and meeting-row writes (userDataStorage).
 */
const VENUE_GATED_EXCHANGES = new Set(['anon_profile', 'pxp']);

export const isVenueGated = (connectionMetadata) =>
  VENUE_GATED_EXCHANGES.has(connectionMetadata?.exchangeType) &&
  connectionMetadata?.venueConfirmed !== true;

/**
 * Metadata clone with the venue stripped for gated display — the baked
 * `location` string can embed the venue name, so it goes too; the renderer
 * falls back to the neighborhood line. Ungated metadata passes through as-is.
 */
export const gateVenueMetadata = (connectionMetadata) => {
  if (!connectionMetadata || !isVenueGated(connectionMetadata)) return connectionMetadata;
  return {
    ...connectionMetadata,
    location: undefined,
    locationData: connectionMetadata.locationData
      ? {
          ...connectionMetadata.locationData,
          venueInfo: {
            ...(connectionMetadata.locationData.venueInfo || {}),
            venue: null,
          },
        }
      : connectionMetadata.locationData,
  };
};

export const formatSystemMessageLocation = (connectionMetadata) => {
  if (!connectionMetadata) return '';

  try {
    const venueInfo = connectionMetadata?.locationData?.venueInfo;
    const venue = venueInfo?.venue;
    const address = venueInfo?.address;

    const venueGated = isVenueGated(connectionMetadata);

    if (venue?.name && !venueGated) return venue.name;
    if (address?.neighborhood) return address.neighborhood;
    if (address?.cityAbbr) return address.cityAbbr;
    if (address?.city) return getCityAbbreviation(address.city);

    // The short-format fallback (and the location string it parses) can embed
    // the venue name — never reach it while the venue is gated.
    if (venueGated) return '';
    return formatLocationShort(connectionMetadata) || '';
  } catch (error) {
    console.error('Error formatting system message location:', error);
    return '';
  }
};

/**
 * Short-address composition rules, in ONE place: street [, neighborhood] [, city].
 * abbreviateCity: the subordinate line abbreviates (so its filter matches
 * formatSystemMessageLocation's primary); the full line keeps the raw city
 * (pre-existing display behavior).
 */
const shortAddressParts = (address, { abbreviateCity = false } = {}) => {
  if (!address) return [];
  const parts = [];
  if (address.streetNumber && address.street) {
    parts.push(`${address.streetNumber} ${address.street}`);
  } else if (address.street) {
    parts.push(address.street);
  }
  if (address.neighborhood) parts.push(address.neighborhood);
  if (address.cityAbbr) {
    parts.push(address.cityAbbr);
  } else if (address.city) {
    parts.push(abbreviateCity ? getCityAbbreviation(address.city) : address.city);
  }
  return parts;
};

/**
 * Short address for display: "8601 N Calle Diamante, Prescott".
 * (Extracted from VenueSelectorMessage's local copy — LocationOverlay keeps its
 * own variant with different semantics: no neighborhood.)
 *
 * @param {Object} address - venueInfo.address components
 * @returns {string}
 */
export const formatAddressShort = (address) => shortAddressParts(address).join(', ');

/**
 * Subordinate address line for a detail-card place display.
 *
 * The no-venue WE MET card shows a primary place line ([pin] + the same string
 * formatSystemMessageLocation gives the chat pill) with the rest of the address
 * below it. This builds that second line: street, neighborhood, city — minus
 * whatever component the primary line already shows, so the town/neighborhood
 * is never repeated ("Prescott" primary → "8601 N Calle Diamante" below;
 * "Williamsburg" primary → "123 Berry St, NYC" below).
 *
 * @param {Object} address - venueInfo.address components
 * @param {string} primaryPlace - what the primary line shows (formatSystemMessageLocation)
 * @returns {string}
 */
export const formatAddressSubordinate = (address, primaryPlace) =>
  shortAddressParts(address, { abbreviateCity: true })
    .filter((p) => p && p !== primaryPlace)
    .join(', ');

/**
 * Generate pre-filled note text from connection metadata
 * Returns: "Williamsburg @ Black Brick Cafe, Tues Dec 30, 5pm"
 * @param {Object} connectionMetadata - Connection metadata containing location and date information
 * @returns {string} - Pre-filled note text
 */
/**
 * The WE MET place derivation — ONE home for the venue/place/address rungs so
 * the renderer (VenueSelectorMessage) and the saved-contact note can never
 * drift: venue name leads when present; otherwise the chat-pill place line
 * leads and the address drops its duplicated component.
 *
 * @param {Object} connectionMetadata - pass the GATED clone for Anon viewers
 * @returns {{primaryPlace: string, subordinate: string, fullAddress: string, noVenuePlace: string}}
 */
export const deriveMetPlaceParts = (connectionMetadata) => {
  const venueInfo = connectionMetadata?.locationData?.venueInfo || {};
  const currentVenue = venueInfo.venue || null;
  const address = venueInfo.address || {};

  const fullAddress = formatAddressShort(address);
  const noVenuePlace =
    !currentVenue?.name && fullAddress ? formatSystemMessageLocation(connectionMetadata) : '';
  const primaryPlace = currentVenue?.name || noVenuePlace;
  const subordinate = fullAddress
    ? noVenuePlace
      ? formatAddressSubordinate(address, noVenuePlace)
      : fullAddress
    : '';

  return { primaryPlace, subordinate, fullAddress, noVenuePlace };
};

export const generateNotePrefill = (
  connectionMetadata,
  { friendlyTime = false, includeAddress = true } = {}
) => {
  // The saved-contact note: the WE MET card's content as ONE line, exactly as
  // the detail page displays it AT SAVE TIME (user call, 2026-07-19 — a
  // re-save after converting picks up the fuller Profile view). Callers on
  // the Anon side pass the GATED metadata clone (gateVenueMetadata) and
  // includeAddress:false, so neither an unconfirmed venue nor the street
  // address lands in the phone's contact record when the page hides them.
  if (!connectionMetadata) return '';

  const { primaryPlace, subordinate } = deriveMetPlaceParts(connectionMetadata);

  const date = connectionMetadata.meetingDate
    ? formatDateTimeForDisplay(
        connectionMetadata.meetingDate,
        connectionMetadata.meetingUtcOffset,
        {
          friendlyTime,
        }
      )
    : '';

  return [primaryPlace, includeAddress ? subordinate : '', date].filter(Boolean).join(', ');
};

/**
 * Format location for the header pill display (consolidated view)
 * Priority:
 * 1. Venue name if available
 * 2. Street name (no number) + neighborhood if available
 * 3. Just street name
 *
 * @param {Object} connectionMetadata - Connection metadata containing location information
 * @returns {string} - Location text for pill display
 */
export const formatLocationPillPrimary = (connectionMetadata) => {
  if (!connectionMetadata) return '';

  try {
    // Venue consent gate (device pass 12: the Anon People card leaked the
    // suggested venue) — the gated pill falls through to the neighborhood
    const venueGated = isVenueGated(connectionMetadata);

    const { venueName, neighborhood, cityAbbr, city } =
      extractLocationComponents(connectionMetadata);

    // Venue-or-neighborhood ONLY, never street (decision 2026-07-19: the card
    // matches the detail page's place line — street detail is offputting)
    if (venueName && !venueGated) {
      return venueName;
    }

    if (neighborhood) {
      return neighborhood;
    }

    const displayCity = cityAbbr || getCityAbbreviation(city);
    if (displayCity) {
      return displayCity;
    }

    // Fallback to the stored location string's first component (legacy
    // unstructured data; never when gated — the string can embed the venue)
    if (connectionMetadata.location && !venueGated) {
      return connectionMetadata.location.split(',')[0];
    }

    return '';
  } catch (error) {
    console.error('Error formatting location pill:', error);
    return '';
  }
};

/**
 * Format location for connection card display (list views only)
 * Compact format: "VenueName, Neighborhood" or "VenueName, City"
 *
 * Rules:
 * 1. If venue: "VenueName, Neighborhood" (or City if no Neighborhood)
 * 2. No venue: "Neighborhood, City" (or just City)
 *
 * @param {Object} connectionMetadata - Connection metadata containing location information
 * @returns {string} - Compact location string for card display
 */
export const formatLocationForCard = (connectionMetadata) => {
  if (!connectionMetadata) return '';

  try {
    // Venue consent gate (device pass 2, 2026-08-08: the sans-chat cards
    // leaked the unconfirmed venue guess on BOTH tiers) — same one-predicate
    // rule as the pill formatter; the gated clone strips venue + the baked
    // location string, so every branch below falls to neighborhood/city.
    const metadata = gateVenueMetadata(connectionMetadata);
    const { venueName, neighborhood, cityAbbr, city } = extractLocationComponents(metadata);
    const displayCity = cityAbbr || getCityAbbreviation(city);

    const components = [];

    if (venueName) {
      // Has venue - venue name is enough of a memory trigger
      components.push(venueName);
    } else {
      // No venue - show neighborhood + city, or just city
      if (neighborhood) {
        components.push(neighborhood);
      }
      if (displayCity) {
        components.push(displayCity);
      }
    }

    if (components.length > 0) {
      return components.join(', ');
    }

    // Fallback to stored location string (the gated clone already stripped it
    // when the venue is gated — the baked string can embed the venue name)
    return metadata.location || '';
  } catch (error) {
    console.error('Error formatting card location:', error);
    return '';
  }
};

/**
 * Format time for the header pill display (compact format)
 * Returns: "Thu 4:30pm" (< 7 days), "Thu Apr 20, 12:55pm" (7+ days),
 * or "Thu Apr 20, 2025, 12:55pm" (365+ days)
 *
 * Punctuation matches formatDateTimeForDisplay (detail "We Met") so the same
 * meeting reads identically everywhere — each aging rung inserts one more
 * comma-separated element (user decision, 2026-07-08).
 *
 * @param {string|Date} dateString - Date to format
 * @param {number} [utcOffsetMinutes] - Pin to the meeting's local wall clock (minutes east of UTC)
 * @returns {string} - Compact time string for pill display
 */
export const formatTimePillShort = (dateString, utcOffsetMinutes) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const { dayOfWeek, month, day, timeStr } = getWallClockParts(date, utcOffsetMinutes);

    // Age rungs compute from the true instant, never the shifted wall clock
    const ageMs = Date.now() - date.getTime();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const yearMs = 365 * 24 * 60 * 60 * 1000;
    if (ageMs >= yearMs) {
      // 365+: identical to the detail "We Met" line by construction
      return formatDateTimeForDisplay(dateString, utcOffsetMinutes);
    }
    if (ageMs >= sevenDaysMs) {
      return `${dayOfWeek} ${month} ${day}, ${timeStr}`;
    }

    return `${dayOfWeek} ${timeStr}`;
  } catch (error) {
    console.error('Error formatting time pill:', error);
    return '';
  }
};

// (formatDatePillShort deleted 2026-07-27: its sole consumer — the Anon
// date-only banner — was replaced by the unified two-line sentence pill.)

// ===== SENTENCE BANNER (exchange-clarity D6) =====

/**
 * The chat pill's preposition — which formatSystemMessageLocation branch
 * produced the place decides it: venue → "at", neighborhood/city → "in",
 * street address → "at". Matching the bare place string against the room's
 * metadata names the branch; an unmatched place (an old banner's venue after
 * a re-meet, a custom venue, legacy composed strings) defaults to "at" —
 * venue-shaped strings are the common unmatched case, and "Met at {place}"
 * degrades more gracefully than "Met in {venue}".
 */
export const metPreposition = (place, connectionMetadata) => {
  const venueInfo = connectionMetadata?.locationData?.venueInfo;
  if (place === venueInfo?.venue?.name) return 'at';
  const address = venueInfo?.address;
  if (address?.neighborhood === place) return 'in';
  if (address?.cityAbbr === place || address?.city === place) return 'in';
  if (address?.city && getCityAbbreviation(address.city) === place) return 'in';
  return 'at';
};

/**
 * "Met at Hoxton Hotel" / "Met in Williamsburg" — the sentence composed at
 * render time from the persisted bare place string + metadata (D6: sentences
 * read as content, data reads as chrome). Persisted content stays the bare
 * place, so historic rooms compose identically.
 */
export const formatMetSentence = (place, connectionMetadata) => {
  if (!place) return '';
  return `Met ${metPreposition(place, connectionMetadata)} ${place}`;
};

/**
 * The detail pages' met place: "Hoxton Hotel in Williamsburg", or the
 * neighbourhood alone when no venue is set (user ruling 2026-08-19).
 *
 * Deliberately NOT formatMomentBanner, which the TEXT card uses: that one
 * returns '' while the venue is still gated, so this line would vanish before a
 * host confirms a place. Here the neighbourhood must still show — the gate
 * downgrades the place, it doesn't hide the meeting.
 *
 * No street address: it can't be edited anywhere in the app today and is often
 * wrong in dense cities, so it's bad data on a card. The location itself stays
 * stored — editing and a map are both open.
 */
export const formatMetPlace = (connectionMetadata) => {
  if (!connectionMetadata) return '';
  const meta = gateVenueMetadata(connectionMetadata);
  const { venueName, neighborhood, cityAbbr, city } = extractLocationComponents(meta);
  const area = neighborhood || cityAbbr || getCityAbbreviation(city) || '';

  if (venueName && area && area !== venueName) return `${venueName} in ${area}`;
  return venueName || area || '';
};

/**
 * The new Detail page's meeting card (signup D1; Drop 7 DETAIL §5): the title
 * names itself — "Met at {venue}", "Met in {neighbourhood}", or just "Met"
 * (never an invented place) — with the date below, plus the neighbourhood
 * when the title is a venue. Reads the GATED clone, like formatMetPlace, so an
 * unconfirmed host venue falls to the neighbourhood rather than leaking.
 *
 * @param {Object} connectionMetadata - a meeting's metadata shape
 * @returns {{connective: string, place: string, sub: string}}
 */
export const formatMeetingCard = (connectionMetadata) => {
  const meta = gateVenueMetadata(connectionMetadata) || {};
  const { venueName, neighborhood, cityAbbr, city } = extractLocationComponents(meta);
  const area = neighborhood || cityAbbr || getCityAbbreviation(city) || '';
  const date = meta.meetingDate
    ? formatDateTimeForDisplay(meta.meetingDate, meta.meetingUtcOffset)
    : '';
  if (venueName) {
    const tail = area && area !== venueName ? area : '';
    return {
      connective: 'Met at ',
      place: venueName,
      sub: [date, tail].filter(Boolean).join(' · '),
    };
  }
  if (area) return { connective: 'Met in ', place: area, sub: date };
  return { connective: 'Met', place: '', sub: date };
};

/**
 * The received Intro Card's place (CARD §5b; SIGNUP_Anon §1c): always
 * "Met in {neighbourhood}" — the area, never the venue, at every stage, so it
 * stays put while everything else on the card streams in (the venue's first
 * appearance is the People card). Reads the gated clone like every display
 * rail; no area known → '' (never an invented place).
 *
 * @param {Object} connectionMetadata
 * @returns {string}
 */
export const formatCardMetLine = (connectionMetadata) => {
  if (!connectionMetadata) return '';
  const { neighborhood, cityAbbr, city } = extractLocationComponents(
    gateVenueMetadata(connectionMetadata)
  );
  const area = neighborhood || cityAbbr || getCityAbbreviation(city) || '';
  return area ? `Met in ${area}` : '';
};

/**
 * The TEXT-variant card's moment banner (variants block plan 2, epic §8;
 * settled as a friendly SENTENCE at device pass 1k, sans "We"):
 * "Met at Moonburger in Williamsburg on Fri Aug 14, 8:05pm".
 * Lives on the rail so the venue consent gate applies by construction (the
 * 2026-08-08 hand-rolled leak class): the preposition rides metPreposition
 * and the venue reads the GATED clone. While the venue is still GATED
 * (host hasn't confirmed/picked a place) the banner doesn't render AT ALL —
 * it appears complete once shared, never neighborhood-first with a silent
 * venue upgrade. Nothing at all → '' (hidden).
 */
export const formatMomentBanner = (connectionMetadata) => {
  if (!connectionMetadata) return '';
  if (isVenueGated(connectionMetadata)) return '';
  const meta = gateVenueMetadata(connectionMetadata);
  const { venueName, neighborhood, cityAbbr, city } = extractLocationComponents(meta);
  const area = neighborhood || cityAbbr || getCityAbbreviation(city) || '';

  const parts = ['Met'];
  if (venueName) {
    parts.push(`${metPreposition(venueName, meta)} ${venueName}`);
    // A place that is venue AND area under one name never doubles up
    if (area && area !== venueName) parts.push(`in ${area}`);
  } else if (area) {
    parts.push(`in ${area}`);
  }

  const date = connectionMetadata.meetingDate
    ? formatDateTimeForDisplay(connectionMetadata.meetingDate, connectionMetadata.meetingUtcOffset)
    : '';
  if (date) parts.push(`on ${date}`);

  return parts.length > 1 ? parts.join(' ') : '';
};

/**
 * The banner's quiet second line (D6; both sides since device pass 1):
 * "Fri, 4:30pm" — comma per the user mock (2026-07-27), THIS surface only;
 * message timestamps and InviteSentNotice keep formatTimePillShort's space
 * format. Only the <7-day rung differs — older rungs DELEGATE to
 * formatTimePillShort so the aging ladder has one home and can't drift.
 * (The Anon-only "Met Friday" no-place floor was retired when the two-line
 * pill unified both sides.)
 */
export const formatBannerDateTimeLine = (dateString, utcOffsetMinutes) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const ageMs = Date.now() - date.getTime();
    if (ageMs < 7 * 24 * 60 * 60 * 1000) {
      const { dayOfWeek, timeStr } = getWallClockParts(date, utcOffsetMinutes);
      return `${dayOfWeek}, ${timeStr}`;
    }
    return formatTimePillShort(dateString, utcOffsetMinutes);
  } catch (error) {
    console.error('Error formatting banner date line:', error);
    return '';
  }
};

/**
 * Extract city from full location string (for privacy - Anon users see city only)
 *
 * Examples:
 * - "Starbucks, 123 Main St, New York, NY" → "New York, NY"
 * - "123 Main St, Upper East Side, NYC" → "Upper East Side, NYC"
 * - "New York, NY" → "New York, NY"
 * - "NYC" → "NYC"
 *
 * @param {string} location - Full location string
 * @returns {string} - City-level location (last 2 comma-separated parts)
 */
export const extractCityFromLocation = (location) => {
  if (!location) return '';
  const parts = location.split(',').map((p) => p.trim());
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }
  return location;
};

/**
 * Format location for Anon user display (city only - privacy)
 *
 * Uses structured metadata when available (cityAbbr > city > neighborhood),
 * otherwise falls back to extractCityFromLocation on the location string.
 *
 * @param {Object} connectionMetadata - Connection metadata containing location information
 * @returns {string} - City-level location string for Anon user display
 */
export const formatLocationForAnon = (connectionMetadata) => {
  if (!connectionMetadata) return '';

  try {
    // Try structured data first (most reliable)
    const venueInfo = connectionMetadata?.locationData?.venueInfo;
    const address = venueInfo?.address;

    if (address) {
      // Priority: cityAbbr > city abbreviation > neighborhood
      if (address.cityAbbr) {
        return address.cityAbbr;
      }
      if (address.city) {
        return getCityAbbreviation(address.city);
      }
      if (address.neighborhood) {
        return address.neighborhood;
      }
    }

    // Fallback to parsing location string
    if (connectionMetadata.location) {
      return extractCityFromLocation(connectionMetadata.location);
    }

    return '';
  } catch (error) {
    console.error('Error formatting location for Anon:', error);
    return connectionMetadata?.location || '';
  }
};
